import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { UsePopoverResult, usePopover } from "../info";
import { TableColumn, TableKey, TableRow } from "./types";

export function createRows<T>(data: T[] | undefined, getKey: (t: T) => TableKey): TableRow<T>[] | undefined {
    return data?.map((d) => ({ data: d, key: getKey(d) }));
}

export function equalWidthColumns<T>(columns: TableColumn<T>[]): TableColumn<T>[] {
    return columns.map((c) => ({ ...c, width: `${Math.round(100 / columns.length)}%` }));
}

export interface FilterState<F> {
    value: F;
    setValue: Dispatch<SetStateAction<F>>;
    reset: () => void;
    isActive: boolean;
}

export interface PopoverFilterState<F> extends FilterState<F> {
    popoverProps: UsePopoverResult;
}

/** Manages state for a single filter value. Returns a stable object — safe to use as a useMemo dep. */
export function useFilterState<F>(defaultValue: F): FilterState<F> {
    const defaultRef = useRef(defaultValue);
    const [value, setValue] = useState<F>(defaultRef.current);
    const reset = useCallback(() => setValue(defaultRef.current), []);
    const isActive = !Object.is(value, defaultRef.current);
    return useMemo(() => ({ value, setValue, reset, isActive }), [value, reset, isActive]);
}

/** Extends useFilterState with popover management for filter-button-style UI. */
export function usePopoverFilter<F>(defaultValue: F): PopoverFilterState<F> {
    const state = useFilterState<F>(defaultValue);
    const popoverProps = usePopover();
    return useMemo(() => ({ ...state, popoverProps }), [state, popoverProps]);
}

export function useCheckboxTableController(initialData?: Set<TableKey>) {
    const [selected, setSelected] = useState<Set<TableKey>>(new Set());
    const [initialDataIsSet, setInitialDataIsSet] = useState(false);

    useEffect(() => {
        if (!initialData || initialDataIsSet) return;
        setInitialDataIsSet(true);
        setSelected(initialData);
    }, [initialData, initialDataIsSet, selected]);

    const clearSelection = useCallback(() => setSelected(new Set()), []);

    return { selected, setSelected, clearSelection };
}
